'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { EditorContent, EditorContext, useEditor } from '@tiptap/react';
import type { Editor } from '@tiptap/react';
// --- Tiptap Core Extensions ---
import { Blockquote } from '@tiptap/extension-blockquote';
import { Bold } from '@tiptap/extension-bold';
import { Code } from '@tiptap/extension-code';
import { CodeBlock } from '@tiptap/extension-code-block';
import { Document } from '@tiptap/extension-document';
import { HardBreak } from '@tiptap/extension-hard-break';
import { Heading } from '@tiptap/extension-heading';
import { ImageWithMaxWidth } from 'shared-components/components/TipTap/tiptap-extension/image-max-width-extension';
import { Italic } from '@tiptap/extension-italic';
import { TableKit } from '@tiptap/extension-table';
import { BulletList, ListItem, ListKeymap, OrderedList, TaskItem, TaskList } from '@tiptap/extension-list';
import { Paragraph } from '@tiptap/extension-paragraph';
import { Strike } from '@tiptap/extension-strike';
import { Subscript } from '@tiptap/extension-subscript';
import { Superscript } from '@tiptap/extension-superscript';
import { Text } from '@tiptap/extension-text';
import { TextAlign } from '@tiptap/extension-text-align';
import { InlineStyle } from 'shared-components/components/TipTap/tiptap-extension/inline-style-extension';
import { Typography } from '@tiptap/extension-typography';
import { Underline } from '@tiptap/extension-underline';
import { Highlight } from '@tiptap/extension-highlight';
import { FileHandler } from '@tiptap/extension-file-handler';

import { Dropcursor, Gapcursor, TrailingNode, UndoRedo } from '@tiptap/extensions';

// --- UI Primitives ---
import { Spacer } from 'shared-components/components/TipTap/tiptap-ui-primitive/spacer';
import {
  Toolbar,
  ToolbarGroup,
  ToolbarSeparator,
} from 'shared-components/components/TipTap/tiptap-ui-primitive/toolbar';

import type { UploadFunction } from 'shared-components/components/TipTap/tiptap-node/image-upload-node/image-upload-node-extension';
import { InputNode } from 'shared-components/components/TipTap/tiptap-node/input-node/input-node-extension';
import { ImageUploadNode } from 'shared-components/components/TipTap/tiptap-node/image-upload-node/image-upload-node-extension';
import { ImageLoadingNode } from 'shared-components/components/TipTap/tiptap-node/image-loading-node/image-loading-node-extension';
import { DivWithClass } from 'shared-components/components/TipTap/tiptap-node/div-node/div-node-extension';
import { HorizontalRule } from 'shared-components/components/TipTap/tiptap-node/horizontal-rule-node/horizontal-rule-node-extension';
import 'shared-components/components/TipTap/tiptap-node/blockquote-node/blockquote-node.scss';
import 'shared-components/components/TipTap/tiptap-node/code-block-node/code-block-node.scss';
import 'shared-components/components/TipTap/tiptap-node/horizontal-rule-node/horizontal-rule-node.scss';
import 'shared-components/components/TipTap/tiptap-node/list-node/list-node.scss';
import 'shared-components/components/TipTap/tiptap-node/image-node/image-node.scss';
import 'shared-components/components/TipTap/tiptap-node/heading-node/heading-node.scss';
import 'shared-components/components/TipTap/tiptap-node/input-node/input-node.scss';
import 'shared-components/components/TipTap/tiptap-node/paragraph-node/paragraph-node.scss';
import 'shared-components/components/TipTap/tiptap-node/image-upload-node/image-upload-node.scss';
import 'shared-components/components/TipTap/tiptap-node/image-loading-node/image-loading-node.scss';

// --- Tiptap UI ---
import { HeadingDropdownMenu } from 'shared-components/components/TipTap/tiptap-ui/heading-dropdown-menu';
import { ListDropdownMenu } from 'shared-components/components/TipTap/tiptap-ui/list-dropdown-menu';
import { BlockquoteButton } from 'shared-components/components/TipTap/tiptap-ui/blockquote-button';
import { CodeBlockButton } from 'shared-components/components/TipTap/tiptap-ui/code-block-button';
import { MarkButton } from 'shared-components/components/TipTap/tiptap-ui/mark-button';
import { TextAlignButton } from 'shared-components/components/TipTap/tiptap-ui/text-align-button';
import { UndoRedoButton } from 'shared-components/components/TipTap/tiptap-ui/undo-redo-button';
import { ImageUploadButton } from 'shared-components/components/TipTap/tiptap-ui/image-upload-button';

// --- Hooks ---
import { useIsBreakpoint } from 'shared-components/hooks/use-is-breakpoint';
import { useWindowSize } from 'shared-components/hooks/use-window-size';
import { useCursorVisibility } from 'shared-components/hooks/use-cursor-visibility';

// --- Lib ---
import { MAX_IMAGE_SIZE, uploadImageWithPlaceholder, validateImageSize } from 'shared-components/lib/tiptap-utils';

// --- Shared UI ---
import NotificationSnackbar from 'shared-components/components/NotificationSnackbar/NotificationSnackbar';

// --- Styles ---
import 'shared-components/components/TipTap/tiptap-templates/simple/tiptap-variables.scss';
import 'shared-components/components/TipTap/tiptap-templates/simple/simple-editor.scss';

type EditorMode = 'basic' | 'full';

const BasicToolbarContent = ({ isMobile }: { isMobile: boolean }) => {
  return (
    <>
      <Spacer />

      <ToolbarGroup>
        <UndoRedoButton action="undo" />
        <UndoRedoButton action="redo" />
      </ToolbarGroup>

      <ToolbarSeparator />

      <ToolbarGroup>
        <HeadingDropdownMenu levels={[1, 2, 3]} portal={isMobile} />
        <ListDropdownMenu types={['bulletList', 'orderedList']} portal={isMobile} />
      </ToolbarGroup>

      <ToolbarSeparator />

      <ToolbarGroup>
        <MarkButton type="bold" />
        <MarkButton type="italic" />
        <MarkButton type="underline" />
      </ToolbarGroup>

      <Spacer />
    </>
  );
};

const MainToolbarContent = ({ isMobile }: { isMobile: boolean }) => {
  return (
    <>
      <Spacer />

      <ToolbarGroup>
        <UndoRedoButton action="undo" />
        <UndoRedoButton action="redo" />
      </ToolbarGroup>

      <ToolbarSeparator />

      <ToolbarGroup>
        <HeadingDropdownMenu levels={[1, 2]} showNormalText portal={isMobile} />
        <MarkButton type="bold" />
        <MarkButton type="italic" />
        <MarkButton type="strike" />
        <MarkButton type="underline" />
      </ToolbarGroup>

      <ToolbarSeparator />

      <ToolbarGroup>
        <TextAlignButton align="left" />
        <TextAlignButton align="center" />
        <TextAlignButton align="right" />
        <TextAlignButton align="justify" />
      </ToolbarGroup>

      <ToolbarSeparator />

      <ToolbarGroup>
        <ListDropdownMenu types={['bulletList', 'orderedList', 'taskList']} portal={isMobile} />
        <BlockquoteButton />
      </ToolbarGroup>

      <ToolbarSeparator />

      <ToolbarGroup>
        <ImageUploadButton />
      </ToolbarGroup>

      <Spacer />
    </>
  );
};

interface SimpleEditorProps {
  mode?: EditorMode;
  initialContent?: string | Record<string, unknown>;
  onEditorReady?: (editor: Editor | null) => void;
  /** Custom toolbar content. When provided, overrides the mode-based toolbar */
  toolbarContent?: React.ReactNode;
  /** Whether the editor is editable. Defaults to true. */
  editable?: boolean;
  /** Function to get a presigned URL for file uploads. */
  imageUpload?: UploadFunction;
  onImageUploadingChange?: (isUploading: boolean) => void;
}

const handleFileUpload = (currentEditor: Editor, files: File[], pos: number): void => {
  const imageUploadExt = currentEditor.extensionManager.extensions.find(
    (e: { name: string }) => e.name === 'imageUpload',
  );
  const maxSize: number = imageUploadExt?.options?.maxSize ?? 0;
  const onError: ((err: Error) => void) | undefined = imageUploadExt?.options?.onError;
  const onUploadStart: (() => void) | undefined = imageUploadExt?.options?.onUploadStart;
  const onUploadEnd: (() => void) | undefined = imageUploadExt?.options?.onUploadEnd;
  const uploadFn = imageUploadExt?.options?.upload as UploadFunction | undefined;

  if (!uploadFn) {
    onError?.(new Error('Image upload is not configured'));
    return;
  }

  files.forEach((file: File) => {
    const sizeError = validateImageSize(file, maxSize);
    if (sizeError) {
      onError?.(sizeError);
      return;
    }

    uploadImageWithPlaceholder(currentEditor, file, pos, uploadFn, onError, onUploadStart, onUploadEnd);
  });
};

const imageTypes = ['image/png', 'image/jpeg', 'image/gif', 'image/webp'];

export const SimpleEditor = ({
  mode = 'full',
  initialContent,
  onEditorReady,
  toolbarContent,
  editable = true,
  imageUpload,
  onImageUploadingChange,
}: SimpleEditorProps): React.ReactElement => {
  const isMobile = useIsBreakpoint();
  const { height } = useWindowSize();
  const [imageError, setImageError] = useState<string | null>(null);
  const toolbarRef = useRef<HTMLDivElement>(null);
  /** Keeps latest imageUpload so FileHandler + toolbar use the same handler without stale closures. */
  const imageUploadRef = useRef(imageUpload);
  useEffect(() => {
    imageUploadRef.current = imageUpload;
  }, [imageUpload]);

  const onImageUploadingChangeRef = useRef(onImageUploadingChange);
  useEffect(() => {
    onImageUploadingChangeRef.current = onImageUploadingChange;
  }, [onImageUploadingChange]);
  const uploadCountRef = useRef(0);

  const handleUploadStart = useCallback(() => {
    uploadCountRef.current += 1;
    if (uploadCountRef.current === 1) {
      onImageUploadingChangeRef.current?.(true);
    }
  }, []);

  const handleUploadEnd = useCallback(() => {
    uploadCountRef.current = Math.max(0, uploadCountRef.current - 1);
    if (uploadCountRef.current === 0) {
      onImageUploadingChangeRef.current?.(false);
    }
  }, []);

  const resolveImageSrc = useCallback<UploadFunction>(async (file, onProgress, abortSignal) => {
    const fn = imageUploadRef.current;
    if (!fn) {
      throw new Error('Image upload is not configured');
    }
    const url = await fn(file, onProgress, abortSignal);
    return url;
  }, []);

  const editor = useEditor({
    immediatelyRender: false,
    editable,
    editorProps: {
      attributes: {
        autocomplete: 'off',
        autocorrect: 'off',
        autocapitalize: 'off',
        'aria-label': 'Main content area, start typing to enter text.',
        class: 'simple-editor',
      },
    },
    extensions: [
      Document,
      Paragraph,
      Text,
      Bold,
      Italic,
      Strike,
      Code,
      Underline,
      HardBreak,
      Blockquote,
      DivWithClass,
      Heading.configure({ levels: [1, 2, 3, 4] }),
      BulletList,
      OrderedList,
      ListItem,
      ListKeymap,
      CodeBlock,
      HorizontalRule,
      InputNode,
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
      InlineStyle.configure({ types: ['heading', 'paragraph', 'divWithClass'] }),
      TaskList,
      TaskItem.configure({ nested: true }),
      Typography,
      Superscript,
      Subscript,
      Highlight.configure({ multicolor: true }),
      Dropcursor,
      Gapcursor,
      TrailingNode,
      UndoRedo,
      TableKit,
      ImageWithMaxWidth.configure({
        resize: {
          enabled: true,
          directions: ['left', 'right', 'top', 'bottom', 'top-left', 'top-right', 'bottom-left', 'bottom-right'],
          minWidth: 50,
          minHeight: 50,
          alwaysPreserveAspectRatio: true,
        },
      }),
      ImageUploadNode.configure({
        accept: imageTypes.join(','),
        maxSize: MAX_IMAGE_SIZE,
        limit: 1,
        upload: resolveImageSrc,
        onError: (err) => setImageError(err.message),
        onUploadStart: handleUploadStart,
        onUploadEnd: handleUploadEnd,
      }),
      ImageLoadingNode,
      FileHandler.configure({
        allowedMimeTypes: imageTypes,
        onDrop: (currentEditor: Editor, files: File[], pos: number) => {
          handleFileUpload(currentEditor, files, pos);
        },
        onPaste: (currentEditor: Editor, files: File[]) => {
          const pos = currentEditor.state.selection.$head.pos;
          handleFileUpload(currentEditor, files, pos);
        },
      }),
    ],
    content: initialContent ?? undefined,
  });

  useEffect(() => {
    if (editor && onEditorReady) {
      onEditorReady(editor);
    }
  }, [editor, onEditorReady]);

  // Update editable state when prop changes
  useEffect(() => {
    if (editor) {
      editor.setEditable(editable);
    }
  }, [editor, editable]);

  const rect = useCursorVisibility({
    editor,
    overlayHeight: toolbarRef.current?.getBoundingClientRect().height ?? 0,
  });

  return (
    <div className="simple-editor-wrapper" style={{ width: '100%' }}>
      <EditorContext.Provider value={{ editor }}>
        <Toolbar
          ref={toolbarRef}
          style={{
            ...(isMobile
              ? {
                  bottom: `calc(100% - ${height - rect.y}px)`,
                }
              : {}),
            backgroundColor: 'white',
          }}>
          {toolbarContent ? (
            toolbarContent
          ) : mode === 'basic' ? (
            <BasicToolbarContent isMobile={isMobile} />
          ) : (
            <MainToolbarContent isMobile={isMobile} />
          )}
        </Toolbar>

        <EditorContent editor={editor} role="presentation" className="simple-editor-content" />
      </EditorContext.Provider>

      <NotificationSnackbar
        open={imageError !== null}
        onClose={() => setImageError(null)}
        message={imageError ?? undefined}
        autoHideDuration={5000}
      />
    </div>
  );
};
